import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SafetyAlarm } from '../components/SafetyAlarm';
import { IpCameraSource, fetchSnapshotBlob } from '../components/liveCameras';

/**
 * Live safety detection — the trained models on a live video stream.
 *
 * The webcam is drawn to a canvas every animation frame (smooth), with detection
 * boxes on top. Detection runs on the server a few times a second (CPU-bound, so
 * not 30fps). With "Blur faces" on, faces are found on those same frames and their
 * regions are blurred on the canvas every frame (GDPR) — the raw webcam is never
 * shown. No events are raised here; this is the monitor eye.
 */

interface Det {
  kind: string;
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
  meta?: { ppe?: { vest?: string; helmet?: string } };
}
interface Face {
  x: number;
  y: number;
  w: number;
  h: number;
}

const DETECTORS = [
  { key: 'person', label: 'People' },
  { key: 'ppe', label: 'PPE' },
  { key: 'fire', label: 'Fire' },
  { key: 'smoke', label: 'Smoke' },
  { key: 'forklift', label: 'Forklift' },
];
const COLOUR: Record<string, string> = {
  person: '#3b82f6',
  fire: '#d03b3b',
  smoke: '#9fb0c0',
  forklift: '#fab219',
  vehicle: '#fab219',
};

interface ZonePoint {
  x: number;
  y: number;
}
interface LiveZone {
  id: number;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  polygon: ZonePoint[] | null;
  rule: string;
}
const ZONE_COLOUR: Record<string, string> = {
  'no-entry': '#d03b3b',
  'all-humans': '#3b82f6',
  ppe: '#fab219',
  occupancy: '#22c3a6',
  'no-vehicles': '#a78bfa',
};
/** A zone's outline — its own polygon, or the rectangle turned into four corners. */
function zonePoints(z: LiveZone): ZonePoint[] {
  if (z.polygon && z.polygon.length >= 3) return z.polygon;
  return [
    { x: z.x, y: z.y },
    { x: z.x + z.w, y: z.y },
    { x: z.x + z.w, y: z.y + z.h },
    { x: z.x, y: z.y + z.h },
  ];
}
/** Ray-cast point-in-polygon on normalised (0..1) coordinates. */
function pointInPoly(px: number, py: number, poly: ZonePoint[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i]!.x;
    const yi = poly[i]!.y;
    const xj = poly[j]!.x;
    const yj = poly[j]!.y;
    const hit = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

export function SafetyLiveView() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scratchRef = useRef<HTMLCanvasElement | null>(null);
  const detsRef = useRef<Det[]>([]);
  const facesRef = useRef<Face[]>([]);
  const rafRef = useRef<number>(0);
  const busyRef = useRef(false);
  // The current IP-camera frame (a decoded snapshot) drawn between detection ticks.
  const ipFrameRef = useRef<ImageBitmap | null>(null);
  // The selected IP camera's zones, drawn over the feed; hot = a person is inside.
  const zonesRef = useRef<LiveZone[]>([]);
  const zoneHotRef = useRef<Set<number>>(new Set());
  const [zones, setZones] = useState<LiveZone[]>([]);
  const [intrusions, setIntrusions] = useState<Array<{ zone: string; count: number }>>([]);
  zonesRef.current = zones;

  // Source: the browser webcam, or a configured IP camera watched via server snapshots.
  const [sourceMode, setSourceMode] = useState<'webcam' | 'ipcam'>('webcam');
  const [ipCamId, setIpCamId] = useState<number | null>(null);
  const modeRef = useRef(sourceMode);
  const ipCamIdRef = useRef(ipCamId);
  modeRef.current = sourceMode;
  ipCamIdRef.current = ipCamId;

  const [running, setRunning] = useState(false);
  const [intervalMs, setIntervalMs] = useState(1200);
  const [kinds, setKinds] = useState<Record<string, boolean>>({ person: true, ppe: true, fire: true, smoke: true, forklift: false });
  const [blurFaces, setBlurFaces] = useState(false);
  const [blurStrength, setBlurStrength] = useState(6);
  const blurRef = useRef(false);
  const strengthRef = useRef(6);
  strengthRef.current = blurStrength;
  const [counts, setCounts] = useState({ persons: 0, missingPpe: 0, fire: 0, smoke: 0, faces: 0 });
  const [latency, setLatency] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');

  blurRef.current = blurFaces;
  const kindsCsv = Object.entries(kinds).filter(([, v]) => v).map(([k]) => k).join(',') || 'person';

  // Continuous render: draw the current source (webcam or IP snapshot) to the
  // canvas, blur faces, draw boxes.
  const render = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    // The drawable source and its native size differ per mode: a live <video> for
    // the webcam, the latest decoded snapshot bitmap for an IP camera.
    const ipFrame = ipFrameRef.current;
    const src: CanvasImageSource | null =
      modeRef.current === 'ipcam' ? ipFrame : video && video.videoWidth ? video : null;
    const srcW = modeRef.current === 'ipcam' ? ipFrame?.width ?? 0 : video?.videoWidth ?? 0;
    const srcH = modeRef.current === 'ipcam' ? ipFrame?.height ?? 0 : video?.videoHeight ?? 0;
    if (src && canvas && srcW && srcH) {
      const W = 960;
      const H = Math.round((W * srcH) / srcW);
      if (canvas.width !== W || canvas.height !== H) {
        canvas.width = W;
        canvas.height = H;
      }
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.filter = 'none';
        ctx.drawImage(src, 0, 0, W, H);

        // Blur each face region (from the last detection), clipped so it doesn't bleed.
        if (blurRef.current) {
          const sx = srcW / W;
          const sy = srcH / H;
          for (const f of facesRef.current) {
            const pad = 0.45 * Math.max(f.w * W, f.h * H);
            const x0 = Math.max(0, f.x * W - pad);
            const y0 = Math.max(0, f.y * H - pad);
            const x1 = Math.min(W, f.x * W + f.w * W + pad);
            const y1 = Math.min(H, f.y * H + f.h * H + pad);
            const bw = x1 - x0;
            const bh = y1 - y0;
            if (bw <= 0 || bh <= 0) continue;
            ctx.save();
            ctx.beginPath();
            ctx.rect(x0, y0, bw, bh);
            ctx.clip();
            // Blur radius scales with the face and the user's strength setting
            // (6 = the heavy default); two passes so even a large face is smeared.
            // Radius grows with the slider: low = light, high = very strong.
            const r = Math.max(2, Math.round((Math.max(bw, bh) * strengthRef.current) / 40));
            ctx.filter = `blur(${r}px)`;
            ctx.drawImage(src, x0 * sx, y0 * sy, bw * sx, bh * sy, x0, y0, bw, bh);
            ctx.drawImage(canvas, x0, y0, bw, bh, x0, y0, bw, bh);
            ctx.restore();
          }
        }

        // Zones over the feed — the rules you're watching. A zone with someone
        // inside flashes red (a live intrusion); the persisted screengrab alarm
        // is raised by the server monitor, not here.
        if (modeRef.current === 'ipcam' && zonesRef.current.length) {
          ctx.filter = 'none';
          ctx.lineWidth = Math.max(2, W / 380);
          ctx.font = `${Math.max(11, Math.round(W / 60))}px system-ui, sans-serif`;
          for (const zn of zonesRef.current) {
            const pts = zonePoints(zn);
            if (pts.length < 2) continue;
            const hot = zoneHotRef.current.has(zn.id);
            const base = ZONE_COLOUR[zn.rule] ?? '#3b82f6';
            const col = hot ? '#ff2d2d' : base;
            ctx.beginPath();
            ctx.moveTo(pts[0]!.x * W, pts[0]!.y * H);
            for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x * W, pts[i]!.y * H);
            ctx.closePath();
            ctx.fillStyle = hot ? 'rgba(255,45,45,0.32)' : `${base}22`;
            ctx.fill();
            ctx.strokeStyle = col;
            ctx.setLineDash(hot ? [] : [6, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
            const lx = pts[0]!.x * W;
            const ly = pts[0]!.y * H;
            const label = `${zn.name}${hot ? ' · IN ZONE' : ''}`;
            const tw = ctx.measureText(label).width + 8;
            const th = Math.max(14, Math.round(W / 48));
            ctx.fillStyle = col;
            ctx.fillRect(lx, Math.max(0, ly - th), tw, th);
            ctx.fillStyle = '#fff';
            ctx.fillText(label, lx + 4, Math.max(11, ly - th / 4));
          }
        }

        // Detection boxes + labels.
        ctx.filter = 'none';
        ctx.lineWidth = Math.max(2, W / 400);
        ctx.font = `${Math.max(11, Math.round(W / 55))}px system-ui, sans-serif`;
        for (const d of detsRef.current) {
          const c = COLOUR[d.kind] ?? '#3b82f6';
          const x = d.x * W;
          const y = d.y * H;
          ctx.strokeStyle = c;
          ctx.strokeRect(x, y, d.w * W, d.h * H);
          let label = d.kind;
          if (d.kind === 'person' && d.meta?.ppe) {
            label = `person  vest:${d.meta.ppe.vest ?? '?'}  helmet:${d.meta.ppe.helmet ?? '?'}`;
          }
          const tw = ctx.measureText(label).width + 8;
          const th = Math.max(14, Math.round(W / 45));
          ctx.fillStyle = c;
          ctx.fillRect(x, Math.max(0, y - th), tw, th);
          ctx.fillStyle = '#fff';
          ctx.fillText(label, x + 4, Math.max(11, y - th / 4));
        }
      }
    }
    rafRef.current = requestAnimationFrame(render);
  }, []);

  // One capture → /api/safety/live → store detections + faces + counts.
  const tick = useCallback(async () => {
    if (busyRef.current) return;
    const ipMode = modeRef.current === 'ipcam';
    const video = videoRef.current;
    if (ipMode ? ipCamIdRef.current == null : !video || (video && video.readyState < 2)) return;
    busyRef.current = true;
    try {
      let blob: Blob | null = null;
      if (ipMode) {
        // Pull one live still from the server; keep it around for the display loop.
        blob = await fetchSnapshotBlob(ipCamIdRef.current!);
        try {
          ipFrameRef.current = await createImageBitmap(blob);
        } catch {
          /* keep the previous frame if this one won't decode */
        }
      } else if (video) {
        if (!scratchRef.current) scratchRef.current = document.createElement('canvas');
        const s = scratchRef.current;
        s.width = video.videoWidth;
        s.height = video.videoHeight;
        s.getContext('2d')?.drawImage(video, 0, 0);
        blob = await new Promise<Blob | null>((res) => s.toBlob(res, 'image/jpeg', 0.7));
      }
      if (!blob) return;
      const form = new FormData();
      form.append('image', blob, 'frame.jpg');
      form.append('kinds', kindsCsv);
      if (blurRef.current) form.append('blurFaces', '1');
      const r = await fetch('/api/safety/live', { method: 'POST', body: form });
      if (!r.ok) throw new Error(`detect ${r.status}`);
      const j = (await r.json()) as { detections: Det[]; faces?: Face[]; latency_ms: number };
      detsRef.current = j.detections ?? [];
      facesRef.current = j.faces ?? [];
      setLatency(j.latency_ms ?? null);
      const persons = detsRef.current.filter((d) => d.kind === 'person');
      setCounts({
        persons: persons.length,
        missingPpe: persons.filter((d) => d.meta?.ppe?.vest === 'no' || d.meta?.ppe?.helmet === 'no').length,
        fire: detsRef.current.filter((d) => d.kind === 'fire').length,
        smoke: detsRef.current.filter((d) => d.kind === 'smoke').length,
        faces: facesRef.current.length,
      });
      // Who is inside a no-entry / all-humans zone right now — for the live flag.
      // (The server monitor is what persists the screengrab alarm; this is the eye.)
      const forklifts = detsRef.current.filter((d) => d.kind === 'forklift' || d.kind === 'vehicle');
      const hot = new Set<number>();
      const intr: Array<{ zone: string; count: number }> = [];
      for (const zn of zonesRef.current) {
        const poly = zonePoints(zn);
        const subjects = zn.rule === 'no-vehicles' ? forklifts : persons;
        if (zn.rule === 'occupancy' || zn.rule === 'ppe') continue;
        const n = subjects.filter((d) => pointInPoly(d.x + d.w / 2, d.y + d.h / 2, poly)).length;
        if (n > 0) {
          hot.add(zn.id);
          intr.push({ zone: zn.name, count: n });
        }
      }
      zoneHotRef.current = hot;
      setIntrusions(intr);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      busyRef.current = false;
    }
  }, [kindsCsv]);

  // Load the chosen IP camera's zones so they can be drawn over the live feed.
  useEffect(() => {
    if (sourceMode !== 'ipcam' || ipCamId == null) {
      setZones([]);
      setIntrusions([]);
      zoneHotRef.current = new Set();
      return;
    }
    fetch(`/api/safety/cameras/${ipCamId}/zones`)
      .then((r) => (r.ok ? r.json() : []))
      .then((zs: LiveZone[]) => setZones(zs))
      .catch(() => setZones([]));
  }, [sourceMode, ipCamId]);

  // The render loop runs for either source. Draw is source-agnostic (see render).
  useEffect(() => {
    if (!running) return;
    rafRef.current = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ipFrameRef.current = null;
      detsRef.current = [];
      facesRef.current = [];
    };
  }, [running, render]);

  // Webcam lifecycle — only when the webcam is the source. Re-acquires on device change.
  useEffect(() => {
    if (!running || sourceMode !== 'webcam') return;
    let stream: MediaStream | null = null;
    navigator.mediaDevices
      .getUserMedia({
        video: deviceId ? { deviceId: { exact: deviceId } } : { width: 1280, height: 720 },
        audio: false,
      })
      .then(async (s) => {
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        // Labels are only exposed after permission is granted, so list now.
        const all = await navigator.mediaDevices.enumerateDevices();
        setDevices(all.filter((d) => d.kind === 'videoinput'));
        if (!deviceId) setDeviceId(s.getVideoTracks()[0]?.getSettings().deviceId ?? '');
      })
      .catch((e) => {
        setError(`Camera: ${(e as Error).message}`);
        setRunning(false);
      });
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [running, sourceMode, deviceId]);

  // Detection loop.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void tick(), intervalMs);
    return () => clearInterval(t);
  }, [running, intervalMs, tick]);

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Live safety</h1>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <SafetyAlarm />
          <Link to="/safety" style={{ fontSize: 13 }}>Monitor</Link>
          <Link to="/safety/events" style={{ fontSize: 13 }}>Events</Link>
        </div>
      </div>
      <p className="muted" style={{ marginTop: -8, maxWidth: 720 }}>
        The trained models on a live camera feed — boxes drawn over the video as it plays. Detection
        runs on the server (CPU), a couple of frames a second; nothing is saved here.
      </p>

      <div className="row" style={{ marginBottom: 12 }}>
        <div className="panel" style={{ minWidth: 110 }}>
          <div className="muted" style={{ fontSize: 12 }}>People</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{counts.persons}</div>
        </div>
        <div className="panel" style={{ minWidth: 110 }}>
          <div className="muted" style={{ fontSize: 12 }}>Missing PPE</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: counts.missingPpe ? 'var(--warn)' : undefined }}>{counts.missingPpe}</div>
        </div>
        <div className="panel" style={{ minWidth: 110 }}>
          <div className="muted" style={{ fontSize: 12 }}>Fire / smoke</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: counts.fire || counts.smoke ? 'var(--bad)' : undefined }}>
            {counts.fire + counts.smoke}
          </div>
        </div>
        {blurFaces && (
          <div className="panel" style={{ minWidth: 110 }}>
            <div className="muted" style={{ fontSize: 12 }}>Faces blurred</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--teal)' }}>{counts.faces}</div>
          </div>
        )}
        <div className="panel" style={{ minWidth: 110 }}>
          <div className="muted" style={{ fontSize: 12 }}>Latency</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{latency != null ? `${latency} ms` : '—'}</div>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 12 }}>{error}</div>}

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13 }}>Source</strong>
          <div className="row" style={{ gap: 6 }}>
            <button
              className={`btn sm ${sourceMode === 'webcam' ? 'primary' : ''}`}
              onClick={() => setSourceMode('webcam')}
            >
              Webcam
            </button>
            <button
              className={`btn sm ${sourceMode === 'ipcam' ? 'primary' : ''}`}
              onClick={() => setSourceMode('ipcam')}
              title="Watch a fixed IP camera on the network (the same cameras used for zones)"
            >
              IP camera
            </button>
          </div>
          {sourceMode === 'ipcam' && (
            <div style={{ flex: '1 1 260px' }}>
              <IpCameraSource cameraId={ipCamId} onPick={setIpCamId} showConnect hidePreview={running} intervalMs={2000} />
            </div>
          )}
        </div>
        {sourceMode === 'ipcam' && running && ipCamId == null && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8, color: 'var(--warn)' }}>
            Pick an IP camera above to start detection.
          </div>
        )}
        {sourceMode === 'ipcam' && ipCamId != null && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            {zones.length > 0
              ? `${zones.length} zone${zones.length > 1 ? 's' : ''} on this camera — drawn over the feed; a red zone means someone is inside. `
              : 'No zones on this camera yet — draw them on '}
            <Link to="/safety/zones">Cameras &amp; zones</Link>.
          </div>
        )}
      </div>

      {intrusions.length > 0 && (
        <div
          className="panel"
          style={{
            marginBottom: 12,
            background: 'color-mix(in srgb, var(--bad) 22%, transparent)',
            borderColor: 'var(--bad)',
            color: 'var(--bad)',
            fontWeight: 700,
          }}
        >
          ⚠ IN ZONE — {intrusions.map((i) => `${i.count}× ${i.zone}`).join(' · ')}. The 24/7 monitor is saving a
          screengrab alarm; see{' '}
          <Link to="/safety/events" style={{ color: 'inherit', textDecoration: 'underline' }}>
            Events
          </Link>
          .
        </div>
      )}

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className={`btn ${running ? '' : 'primary'}`}
            onClick={() => setRunning((r) => !r)}
            disabled={sourceMode === 'ipcam' && ipCamId == null && !running}
          >
            {running ? '■ Stop' : sourceMode === 'ipcam' ? '▶ Start monitoring' : '▶ Start camera'}
          </button>
          {running && sourceMode === 'webcam' && devices.length > 1 && (
            <select
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              style={{ maxWidth: 240, fontSize: 13 }}
              title="Choose camera"
            >
              {devices.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>
                  {d.label || `Camera ${i + 1}`}
                </option>
              ))}
            </select>
          )}
          {/* A big, obvious privacy switch — not a checkbox lost among the others. */}
          <button
            onClick={() => setBlurFaces((b) => !b)}
            className="btn"
            style={{
              fontWeight: 700,
              borderColor: blurFaces ? 'var(--teal)' : 'var(--border)',
              background: blurFaces ? 'color-mix(in srgb, var(--teal) 20%, transparent)' : undefined,
              color: blurFaces ? 'var(--teal)' : 'var(--dim)',
            }}
            title="Blur every detected face on the live view (GDPR)"
          >
            Blur faces: {blurFaces ? 'ON' : 'OFF'}
          </button>
          {blurFaces && (
            <label className="row" style={{ gap: 6, fontSize: 13 }}>
              Strength
              <span className="muted" style={{ fontSize: 11 }}>light</span>
              <input type="range" dir="ltr" min={1} max={10} step={1} value={blurStrength} onChange={(e) => setBlurStrength(Number(e.target.value))} style={{ direction: 'ltr' }} />
              <span className="muted" style={{ fontSize: 11 }}>strong</span>
              <span className="muted">{blurStrength}</span>
            </label>
          )}
          <label className="row" style={{ gap: 6, fontSize: 13 }}>
            Rate
            <input type="range" min={500} max={5000} step={250} value={intervalMs} onChange={(e) => setIntervalMs(Number(e.target.value))} />
            <span className="muted">{(1000 / intervalMs).toFixed(1)}/s</span>
          </label>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {DETECTORS.map((d) => (
              <label key={d.key} className="row" style={{ gap: 4, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={kinds[d.key] ?? false} onChange={() => setKinds((p) => ({ ...p, [d.key]: !p[d.key] }))} />
                {d.label}
              </label>
            ))}
          </div>
        </div>
        {blurFaces && (
          <div className="muted" style={{ fontSize: 11, marginTop: 8, color: 'var(--teal)' }}>
            Faces are blurred on every frame using the latest detection (~1/s). Fast movement can
            briefly outrun it; raise the rate for tighter coverage.
          </div>
        )}
      </div>

      <div className="panel" style={{ display: 'inline-block', maxWidth: '100%', padding: 0, overflow: 'hidden', lineHeight: 0 }}>
        {/* Source only — the canvas is what's shown, so faces can be blurred before display. */}
        <video ref={videoRef} autoPlay muted playsInline style={{ display: 'none' }} />
        <canvas ref={canvasRef} style={{ maxWidth: '100%', width: 960, background: '#000', display: 'block' }} />
        {!running && (
          <div className="muted" style={{ padding: 24, fontSize: 14, lineHeight: 1.5 }}>
            {sourceMode === 'ipcam'
              ? 'Pick an IP camera above, then press “Start monitoring”.'
              : 'Press “Start camera” to begin live detection.'}
          </div>
        )}
      </div>
    </div>
  );
}
